import {Component, inject, OnInit} from '@angular/core';
import {DatePipe, DecimalPipe} from '@angular/common';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {MatIcon} from '@angular/material/icon';
import { AuthenticationService } from '@core/auth/authentication.service';
import { HasPermissionDirective } from '@shared/directives/has-permission.directive';
import {Spinner} from '@shared/components/ui/spinner/spinner';
import {Badge} from '@shared/components/ui/badge/badge';
import {ButtonComponent} from '@shared/components/ui/button/button.component';
import {IconButton} from '@shared/components/ui/icon-button/icon-button';
import {DialogService} from '@shared/services/dialog.service';
import {ErrorService} from '@shared/services/error.service';
import {ClientesService} from '../clientes.service';
import {Cliente, ContratoCliente} from '../clientes.interface';
import {Establecimientos} from '../../establecimientos/establecimientos';
import {ContactosAdministrativosService} from '../../contactos-administrativos/contactos-administrativos.service';
import {ContactoAdministrativo} from '../../contactos-administrativos/contactos-administrativos.interface';
import {ObjetivosService} from '../objetivos/objetivos.service';
import {Objetivos} from '../objetivos/objetivos.interface';
import {PresupuestosClienteService} from '../presupuestos/presupuestos-cliente.service';
import {PresupuestoCliente} from '../presupuestos/presupuestos.interface';
import {DatosClienteModal} from './datos-cliente-modal';
import {SeccionCliente, SeccionClienteModal} from './seccion-cliente-modal';
// Antes la ficha era una cabecera editable + seis pestañas. Desde 2026-10-05 los
// establecimientos van a la vista y el resto se abre en modales.
// import {Agendamientos} from '../agendamientos/agendamientos';
// import {ResumenCuentas} from '../resumen-cuentas/resumen-cuentas';

@Component({
  selector: 'app-detalle-cliente',
  imports: [
    HasPermissionDirective,
    RouterLink,
    Spinner,
    Badge,
    ButtonComponent,
    IconButton,
    MatIcon,
    DatePipe,
    DecimalPipe,
    Establecimientos,
  ],
  templateUrl: './detalle-cliente.html',
})
export class DetalleCliente implements OnInit {
  readonly permissionAuth = inject(AuthenticationService);

  idCliente!: number;
  loading = false;
  cliente: Cliente | null = null;

  // Resúmenes de las secciones que se abren en modal
  contactoPrincipal: ContactoAdministrativo | null = null;
  totalContactos = 0;
  contratoActivo: ContratoCliente | null = null;
  objetivoVigente: Objetivos | null = null;
  totalObjetivos = 0;
  ultimoPresupuesto: PresupuestoCliente | null = null;
  totalPresupuestos = 0;

  private route = inject(ActivatedRoute);
  private dialogService = inject(DialogService);
  private errorService = inject(ErrorService);
  private clientesService = inject(ClientesService);
  private contactosService = inject(ContactosAdministrativosService);
  private objetivosService = inject(ObjetivosService);
  private presupuestosService = inject(PresupuestosClienteService);

  get nombre(): string {
    const detalle = this.cliente?.persona.persona_tipo_detalle;
    if (!detalle) return 'Cliente';
    // Los clientes nuevos no tienen apellido: el nombre completo va entero en `nombre`
    return [detalle.nombre, detalle.apellido].filter(Boolean).join(' ');
  }

  /** `documento_completo` viene como numero/tipo/pais (sin país si se cargó sin ubicación). */
  private get partesDocumento(): string[] {
    return (this.cliente?.persona.documento_principal?.documento_completo ?? '').split('/');
  }

  get documento(): string {
    const [numero, tipo] = this.partesDocumento;
    return [tipo, numero].filter(Boolean).join(' ') || '-';
  }

  get pais(): string {
    return this.partesDocumento[2] || '-';
  }

  ngOnInit(): void {
    this.idCliente = Number(this.route.snapshot.paramMap.get('id'));
    this.getCliente();
    this.getResumenes();
  }

  getCliente(): void {
    this.loading = !this.cliente;
    this.clientesService.show(this.idCliente).subscribe({
      next: (response) => {
        if (response.status) this.cliente = response.data;
      },
      error: (error) => this.errorService.handleError(error),
    }).add(() => this.loading = false);
  }

  private getResumenes(): void {
    this.contactosService.index({cli_cliente_id: this.idCliente, activo: 'true', per_page: 50}).subscribe({
      next: (response) => {
        const contactos = response.data.data;
        // Primero un administrativo; si no hay, el primero de un establecimiento
        this.contactoPrincipal = contactos.find(c => c.origen === 'A') ?? contactos[0] ?? null;
        this.totalContactos = response.data.pagination.total;
      },
      error: (error) => this.errorService.handleError(error),
    });

    this.clientesService.getContratoActivo(this.idCliente).subscribe({
      next: (response) => this.contratoActivo = response.status && response.data ? response.data : null,
      error: (error) => this.errorService.handleError(error),
    });

    this.objetivosService.index(this.idCliente).subscribe({
      next: (response) => {
        const objetivos = response.data ?? [];
        this.objetivoVigente = objetivos.find(o => o.periodo?.activo) ?? objetivos[0] ?? null;
        this.totalObjetivos = objetivos.length;
      },
      error: (error) => this.errorService.handleError(error),
    });

    // El listado viene ordenado por fecha descendente
    this.presupuestosService.index(this.idCliente, 1, 1).subscribe({
      next: (response) => {
        this.ultimoPresupuesto = response.data.data[0] ?? null;
        this.totalPresupuestos = response.data.pagination.total;
      },
      error: (error) => this.errorService.handleError(error),
    });
  }

  editarCliente(): void {
    if (!this.permissionAuth.canEdit('clientes')) return;
    this.dialogService.openForm(DatosClienteModal, {size: 'xl', data: this.idCliente})
      .afterClosed()
      .subscribe((cambio) => {
        if (cambio) this.getCliente();
      });
  }

  abrirSeccion(seccion: SeccionCliente, nuevo = false): void {
    if (nuevo && !this.permissionAuth.canCreate('clientes')) return;
    this.dialogService.openForm(SeccionClienteModal, {size: 'xl', data: {seccion, clienteId: this.idCliente, cliente: this.cliente, nuevo}})
      .afterClosed()
      .subscribe(() => {
        // La facturación viaja dentro del cliente
        if (seccion === 'facturacion') this.getCliente();
        this.getResumenes();
      });
  }

  goToProspecto(): void {
    if (!this.cliente?.conversiones[0]) return;
    const prospectoId = this.cliente.conversiones[0].crm_prospecto_id;

    const url = `/#/crm/prospectos/${prospectoId}`;
    window.open(url, '_blank');
  }
}
